/**
 * Returns the max allowed value for the given rebalance variable from all positions.
 * Supported variables: "Yield", "Duration", "S&P Rating", "Moody's Rating"
 * For ratings, finds the max 'value' from the rating objects.
 * @param {string} rebalanceType
 * @param {Array} allPositions
 * @param {Array} ratingOptions (optional) - for ratings, pass the options array [{rating, value}]
 * @returns {number|null}
 */
export function getMaxAllowedValueForRebalance(rebalanceType, allPositions, ratingOptions = []) {
  if (!allPositions || allPositions.length === 0) return null;
  if (
    (rebalanceType === "S&P Rating" || rebalanceType === "Moody's Rating") &&
    (!Array.isArray(ratingOptions) || !ratingOptions.every(opt => opt.hasOwnProperty('rating') && typeof opt.value === 'number'))
  ) {
    throw new Error("Invalid ratingOptions: Must be an array of objects containing 'rating' and a numeric 'value'.");
  }

  switch (rebalanceType) {
    case "Yield": {
      const values = allPositions
        .map(pos => typeof pos.yield === "number" ? pos.yield : Number(pos.yield))
        .filter(val => !isNaN(val));
      if (values.length === 0) return null;
      const max = Math.max(...values);
      return isFinite(max) ? max : null;
    }
    case "Duration": {
      const values = allPositions
        .map(pos => typeof pos.duration === "number" ? pos.duration : Number(pos.duration))
        .filter(val => val !== null && !isNaN(val));
      if (values.length === 0) return null;
      const max = Math.max(...values);
      return isFinite(max) ? max : null;
    }
    case "DV01": {
      // Don't calculate max from bonds for DV01, return a high limit
      return 10000000;
    }
    case "S&P Rating": {
      if (!ratingOptions.length) return null;
      const values = allPositions
        .map(pos => {
          const ratingObj = ratingOptions.find(opt => opt.rating === pos.spRating || opt.rating === pos.ratingSnp);
          return ratingObj ? ratingObj.value : null;
        })
        .filter(val => val !== null && val !== undefined);
      if (values.length === 0) return null;
      const min = Math.min(...values);
      return isFinite(min) ? min : null;
    }
    case "Moody's Rating": {
      if (!ratingOptions.length) return null;
      const values = allPositions
        .map(pos => {
          const ratingObj = ratingOptions.find(opt => opt.rating === pos.moodyRating || opt.rating === pos.ratingMoody);
          return ratingObj ? ratingObj.value : null;
        })
        .filter(val => val !== null && val !== undefined);
      if (values.length === 0) return null;
      const min = Math.min(...values);
      return isFinite(min) ? min : null;
    }
    default:
      return null;
  }
}
